'use client'

import { useSyncExternalStore } from 'react'

type BreakpointState = {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
}

const MOBILE_QUERY = `(max-width: ${768 - 1}px)`
const TABLET_QUERY = `(min-width: ${768}px) and (max-width: ${1024 - 1}px)`
const DESKTOP_QUERY = `(min-width: ${1024}px)`

const serverSnapshot: BreakpointState = {
  isMobile: false,
  isTablet: false,
  isDesktop: false,
}

let cachedSnapshot: BreakpointState = serverSnapshot

function readBreakpointState(): BreakpointState {
  if (typeof window === 'undefined') {
    return serverSnapshot
  }

  const nextSnapshot: BreakpointState = {
    isMobile: window.matchMedia(MOBILE_QUERY).matches,
    isTablet: window.matchMedia(TABLET_QUERY).matches,
    isDesktop: window.matchMedia(DESKTOP_QUERY).matches,
  }

  if (
    nextSnapshot.isMobile === cachedSnapshot.isMobile &&
    nextSnapshot.isTablet === cachedSnapshot.isTablet &&
    nextSnapshot.isDesktop === cachedSnapshot.isDesktop
  ) {
    return cachedSnapshot
  }

  cachedSnapshot = nextSnapshot
  return cachedSnapshot
}

function subscribe(onStoreChange: () => void): () => void {
  const queries = [window.matchMedia(MOBILE_QUERY), window.matchMedia(TABLET_QUERY), window.matchMedia(DESKTOP_QUERY)]

  queries.forEach((query) => query.addEventListener('change', onStoreChange))
  window.addEventListener('resize', onStoreChange)

  return () => {
    queries.forEach((query) => query.removeEventListener('change', onStoreChange))
    window.removeEventListener('resize', onStoreChange)
  }
}

export function useBreakpoint(): BreakpointState {
  return useSyncExternalStore(subscribe, readBreakpointState, () => serverSnapshot)
}
